import React from 'react';
import { useFormikContext } from 'formik';

import { useStyles } from '~/assets/styles';
import { filterEnabledContactRoles, getAllOrganizationWideRoles } from '~/components/communications/ContactUtils';
import ContactTextFieldFormik from '~/components/ContactTextFieldFormik';
import MenuItem from '~/components/core/Atomic/MenuItem';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import Text from '~/components/core/TextComponents/Text';
import useCurrentUserRoles from '~/components/hooks/useCurrentUserRoles';
import useOrganization from '~/components/OrganizationContext';
import TextFieldFormikUntyped from '~/components/TextFieldFormik';
import type { OrganizationContactRolesDict } from '~/server_shared/openapi/data-contracts';
import { stringCmp } from '~/Utils';

import type { MoiAssigneeStrategy, MoiAssignmentRuleFormValues } from './formValues';
import { FIELD_IDS } from './formValues';
import { TEXTS } from './texts';

interface TextFieldFormikSelectProps {
  id: string;
  label: string;
  className: string;
  select: boolean;
  fullWidth: boolean;
  disabled: boolean;
  children: React.ReactNode;
}
// Assertion over a still-untyped JS component, which the TS rule allows for gradual migration: it
// only narrows the props this file passes, so a wrong prop name or type is still a compile error.
const TextFieldFormik = TextFieldFormikUntyped as React.ComponentType<TextFieldFormikSelectProps>;

interface MoiAssigneeFieldsFormikProps {
  /** The organization being configured, whose contacts the picker searches. */
  organizationId: number;
  disabled: boolean;
}

const MoiAssigneeFieldsFormik: React.FC<MoiAssigneeFieldsFormikProps> = ({ organizationId, disabled }) => {
  const { values, setValues } = useFormikContext<MoiAssignmentRuleFormValues>();
  // Both hooks are exported from JSX files without types; cast at the boundary.
  const { organizationContactRolesDict } = useOrganization() as {
    organizationContactRolesDict: OrganizationContactRolesDict;
  };
  const { isUserFromSuperOrganization } = useCurrentUserRoles() as { isUserFromSuperOrganization?: boolean };
  const { classes } = useStyles();

  // The roles the MOI stepper's service-provider search accepts, the same set the picker below is
  // given, less the ones the organization switched off.
  const organizationWideRoles = getAllOrganizationWideRoles(organizationContactRolesDict);
  const roleKeys: string[] = filterEnabledContactRoles(organizationContactRolesDict, organizationWideRoles).sort(
    (a: string, b: string) => stringCmp(organizationContactRolesDict[a].desc, organizationContactRolesDict[b].desc)
  );

  // One `setValues` that carries the strategy too, rather than a `setFieldValue` per cleared field:
  // each `setFieldValue` validates the last-rendered values with only its own field changed, and
  // Formik neither cancels nor orders those validations, so a later-resolving one can leave a stale
  // "Required" behind (see `ContactTextFieldFormik`). Carrying the strategy keeps the result right
  // whichever runs first — this or the radio's own write of the clicked option.
  const handleStrategyClick = (strategy: MoiAssigneeStrategy) => {
    setValues(
      strategy === 'contact_role'
        ? {
            ...values,
            [FIELD_IDS.ASSIGNEE_STRATEGY]: strategy,
            [FIELD_IDS.CONTACT_ID]: '',
            [FIELD_IDS.CONTACT_FULL_NAME]: '',
            [FIELD_IDS.CONTACT]: null,
          }
        : { ...values, [FIELD_IDS.ASSIGNEE_STRATEGY]: strategy, [FIELD_IDS.CONTACT_ROLE]: '' }
    );
  };

  return (
    <div className="flex flex-col gap-12">
      <RadioButtonGroupFormik
        id={FIELD_IDS.ASSIGNEE_STRATEGY}
        direction="row"
        options={[
          { optionValue: 'static', text: TEXTS.STRATEGY_STATIC },
          { optionValue: 'contact_role', text: TEXTS.STRATEGY_ROLE },
        ]}
        onClick={handleStrategyClick}
        // The group has no `disabled` prop; `showOnly` is what disables every option at once.
        showOnly={disabled}
      />
      {values.assignee_strategy === 'static' ? (
        // `ContactTextFieldFormik` reads `disabled` only in its read-only mode, so it is not passed.
        <ContactTextFieldFormik
          id={FIELD_IDS.CONTACT}
          label={TEXTS.CONTACT_LABEL}
          outOfContactsContextSearch
          // The out-of-claim search throws for a user outside the super organization who hands it an
          // organization to search, and without one it searches the user's own organization. That
          // is the organization being configured for a customer's admin, but not for an internal
          // admin, whose own is the super organization.
          overrideOfContactsContextSearchOrganizationId={isUserFromSuperOrganization ? organizationId : undefined}
          acceptedRoles={organizationWideRoles}
          disallowNew
          fixedSearchResults
          fullWidth
          nullIfEmpty
        />
      ) : (
        <div>
          <TextFieldFormik
            id={FIELD_IDS.CONTACT_ROLE}
            label={TEXTS.ROLE_LABEL}
            className={classes.textField}
            select
            fullWidth
            disabled={disabled}
          >
            {roleKeys.map((roleKey) => (
              <MenuItem key={roleKey} value={roleKey}>
                {organizationContactRolesDict[roleKey].desc}
              </MenuItem>
            ))}
          </TextFieldFormik>
          {/* A caption of its own rather than the select's `helperText`, which `TextFieldFormik`
              replaces with the field's error — and the error shows while a role is still being
              chosen, which is when the help is needed. */}
          <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {TEXTS.ROLE_HELP}
          </Text>
        </div>
      )}
    </div>
  );
};

export default MoiAssigneeFieldsFormik;
